import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useUniqueTableName from '../../../hooks/useUniqueTableName';
import useBaseTableProps from '../../../hooks/useBaseTableProps';
import useActionsFirstColumnOrder from '../../../hooks/useActionsFirstColumnOrder';
import { getUrlWithSelectedPropertyId } from '../../../utils/navigation-helpers';
import {
  HEADERS,
  PROPERTY_PATH_MAP,
  AGREEMENT_TYPE_OPTIONS,
} from './constants';
import { processTableFilterOptions } from '../../../utils/table-helpers';
import messages from './messages';
import parentMessages from '../messages';
import ApprovedAmountCell from '../ApprovedAmountCell';
import { VARIANCE_CELL_SX } from '../constants';
import { getFirstMatchingReason, canEditApprovedAmount } from '../utils';

const TRACS_STATUS_TO_LABEL = {
  NOT_SUBMITTED: 'Not Submitted',
  SUBMITTED: 'Submitted',
  SUCCESS: 'Success',
  ERROR: 'Error',
  QUEUED: 'Queued for Submission',
};

const normalizeAgreementTypeCode = (type) => {
  return (
    AGREEMENT_TYPE_OPTIONS.find(
      (option) => option.value.toLowerCase() === type?.[0]?.toLowerCase(),
    )?.text ?? AGREEMENT_TYPE_OPTIONS[2].text
  );
};

function toStatusKey(s) {
  if (s == null || s === '') return '';
  const key = String(s).replace(/\s+/g, '_').toUpperCase();
  return key === 'QUEUED_FOR_SUBMISSION' ? 'QUEUED' : key;
}

function canDeleteRepayment(voucherSubmissionStatus) {
  if (voucherSubmissionStatus === 'Paid') return false;
  return true;
}

export function normalizeRepaymentAgreementForTable(agreement) {
  const payments = agreement?.payments;
  const payment = Array.isArray(payments) ? payments[0] : undefined;
  const agreementTypeDisplay = normalizeAgreementTypeCode(
    agreement?.agreementType,
  );
  const rawStatus = agreement?.status ?? payment?.status ?? '';
  const statusKey = toStatusKey(rawStatus);
  const tracsStatus =
    TRACS_STATUS_TO_LABEL[rawStatus] ??
    TRACS_STATUS_TO_LABEL[statusKey] ??
    (rawStatus || 'Not Submitted');

  const unitObj = agreement?.unit;
  const unitDisplay =
    typeof unitObj === 'object' && unitObj !== null
      ? unitObj.number || unitObj.id || ''
      : typeof agreement?.unitNumber === 'string'
      ? agreement.unitNumber
      : agreement?.unitId ?? '';

  const hohObj =
    agreement?.household?.headOfHousehold ?? agreement?.headOfHousehold;
  const headOfHouseholdDisplay =
    typeof hohObj === 'object' && hohObj !== null
      ? [hohObj.firstName, hohObj.lastName].filter(Boolean).join(' ').trim() ||
        ''
      : typeof agreement?.headOfHouseholdName === 'string'
      ? agreement.headOfHouseholdName
      : '';

  const residentId =
    agreement?.household?.headOfHousehold?.resident?.id ??
    agreement?.household?.headOfHousehold?.id ??
    agreement?.residentId;

  const voucherRepaymentRowId =
    agreement?.voucherRepaymentRowId ?? agreement?.id;
  const agreementUuid = agreement?.id;

  return {
    id: voucherRepaymentRowId,
    voucherRepaymentRowId,
    agreementUuid,
    unitId: agreement?.unitId,
    unit: unitDisplay,
    hudUnitNumber: unitObj?.hudNumber || '',
    headOfHousehold: headOfHouseholdDisplay,
    residentId: residentId ?? '',
    agreementId: agreement?.repaymentAgreementId ?? '',
    agreementType: agreementTypeDisplay,
    agreementTypeCode: agreement?.agreementType,
    agreementStatus: tracsStatus,
    agreementDate: agreement?.agreementDate,
    beginningBalance: agreement?.beginningBalance ?? 0,
    agreementAmount: agreement?.agreementAmount ?? 0,
    endingBalance: agreement?.endingBalance ?? agreement?.currentBalance ?? 0,
    amountRetained: agreement?.amountRetained ?? 0,
    requestedAmount: agreement?.amountRequested ?? 0,
    approvedAmount: agreement?.approvedAmount ?? 0,
    agreementBalance:
      agreement?.endingBalance ?? agreement?.currentBalance ?? 0,
    agreementChangeAmount: agreement?.agreementChangeAmount ?? 0,
    totalPayment: agreement?.totalPayment ?? 0,
    paymentsRemaining: 0,
    tracsStatus,
    tracsMessage: '',
    isCarriedOver: Boolean(agreement?.isCarriedOver),
    canModify: Boolean(agreement?.canModify),
    canDelete: Boolean(agreement?.canDelete),
  };
}

function useBuildFilterOptions({ results }) {
  return useMemo(() => {
    const options = {
      agreementType: [],
    };

    if (results) {
      results.forEach((result) => {
        if (result.agreementType) {
          options.agreementType.push(result.agreementType);
        }
      });
    }

    processTableFilterOptions(options);

    return options;
  }, [results]);
}

function useHeaders({ results }) {
  const filterOptions = useBuildFilterOptions({ results });

  return useMemo(() => {
    const headers = HEADERS.map((header) => ({
      ...header,
      filterOptions: header.filterOptions ?? filterOptions[header.id],
    }));

    return headers;
  }, [filterOptions]);
}

function useRows({
  repaymentAgreements,
  onDelete,
  onModify,
  voucherSubmissionStatus,
  hasVariance,
  onSaveApprovedAmount,
  intl,
  isReadOnly,
  canManage,
}) {
  return useMemo(() => {
    return repaymentAgreements.map((row) => {
      const isPermissionDisabled = !canManage;
      const deleteEnabled =
        canDeleteRepayment(voucherSubmissionStatus) && !!row.canDelete;
      const canModify = !!row.canModify;

      return {
        id: row.id,
        unit: {
          variant: 'link',
          to: row.unitId
            ? getUrlWithSelectedPropertyId(`/unit/${row.unitId}`)
            : undefined,
          value: row.unit,
        },
        hudUnitNumber: {
          value: row.hudUnitNumber,
        },
        headOfHousehold: {
          variant: 'link',
          to: row.residentId
            ? getUrlWithSelectedPropertyId(`/resident/${row.residentId}`)
            : undefined,
          value: row.headOfHousehold,
        },
        agreementId: {
          value: row.agreementId,
        },
        agreementType: {
          value: normalizeAgreementTypeCode(row.agreementType),
        },
        agreementDate: {
          variant: 'date',
          value: row.agreementDate,
        },
        agreementAmount: {
          variant: 'currency',
          value: row.agreementAmount,
        },
        endingBalance: {
          variant: 'currency',
          value: row.endingBalance,
        },
        amountRetained: {
          variant: 'currency',
          value: row.amountRetained,
        },
        requestedAmount: {
          variant: 'currency',
          value: row.requestedAmount,
        },
        approvedAmount: canEditApprovedAmount(
          hasVariance,
          isReadOnly,
          canManage,
        )
          ? {
              sx: VARIANCE_CELL_SX,
              value: (
                <ApprovedAmountCell
                  key={row.id}
                  initialValue={row.approvedAmount}
                  onSave={(amount) => onSaveApprovedAmount?.(row, amount)}
                />
              ),
            }
          : {
              variant: 'currency',
              value: row.approvedAmount,
            },
        agreementBalance: {
          variant: 'currency',
          value: row.agreementBalance,
        },
        agreementChangeAmount: {
          variant: 'currency',
          value: row.agreementChangeAmount,
        },
        totalPayment: {
          variant: 'currency',
          value: row.totalPayment,
        },
        paymentsRemaining: {
          variant: 'number',
          value: row.paymentsRemaining,
        },
        tracsMessage: {
          value: row.tracsMessage,
        },
        actions: {
          variant: 'menu',
          name: 'actions',
          iconName: 'BoltIcon',
          color: 'primary',
          closeOnClick: true,
          options: [
            {
              value: 'modify',
              text: <FormattedMessage {...messages.modify} />,
              disabled: isPermissionDisabled || isReadOnly || !canModify,
              tooltip: getFirstMatchingReason([
                {
                  condition: isPermissionDisabled,
                  message: intl.formatMessage(
                    parentMessages.noPermissionTooltip,
                  ),
                },
                {
                  condition: isReadOnly || !canModify,
                  message: intl.formatMessage(messages.modifyDisabledTooltip),
                },
              ]),
              tooltipProps: {
                placement: 'left-end',
                sx: {
                  width: 'fit-content',
                },
              },
              onClick: () => {
                if (canModify && onModify) onModify(row);
              },
            },
            {
              value: 'delete',
              text: <FormattedMessage {...messages.delete} />,
              disabled: isPermissionDisabled || !deleteEnabled || isReadOnly,
              tooltip: getFirstMatchingReason([
                {
                  condition: isPermissionDisabled,
                  message: intl.formatMessage(
                    parentMessages.noPermissionTooltip,
                  ),
                },
                {
                  condition: !deleteEnabled,
                  message: intl.formatMessage(messages.deleteDisabledTooltip),
                },
              ]),
              tooltipProps: {
                placement: 'left-start',
                sx: {
                  width: 'fit-content',
                },
              },
              onClick: () => {
                if (deleteEnabled && onDelete) onDelete(row);
              },
            },
          ],
        },
      };
    });
  }, [
    repaymentAgreements,
    voucherSubmissionStatus,
    hasVariance,
    isReadOnly,
    intl,
    onSaveApprovedAmount,
    onModify,
    onDelete,
    canManage,
  ]);
}

export function useRepaymentAgreementsTable({
  isReadOnly,
  repaymentAgreements = [],
  onDelete,
  onModify,
  voucherSubmissionStatus,
  intl,
  hasVariance = false,
  onSaveApprovedAmount,
  canManage,
}) {
  const name = useUniqueTableName('repayment-agreements-table');
  const baseTableProps = useBaseTableProps();

  const searchableRepaymentAgreements = useMemo(() => {
    return repaymentAgreements.map((row) => ({
      ...row,
      agreementTypeSearch: normalizeAgreementTypeCode(row.agreementType),
    }));
  }, [repaymentAgreements]);

  const headers = useHeaders({
    results: repaymentAgreements,
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'agreementDate',
    initialOrder: 'DESC',
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: searchableRepaymentAgreements,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const rows = useRows({
    isReadOnly,
    repaymentAgreements: sortedAndFilteredResults,
    onDelete,
    onModify,
    voucherSubmissionStatus,
    hasVariance,
    onSaveApprovedAmount,
    intl,
    canManage,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  return {
    baseTableProps,
    allColumnsHidden,
    columnOptions,
    count: rows.length,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    isLoading: false,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount: repaymentAgreements.length,
  };
}
